import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/common/ProtectedRoute';
import LoadingSpinner from './components/common/LoadingSpinner';
import { useConfig } from './context/ConfigContext';

/** Guard: /formation-compare is only available on football.kridaclub.com */
function FootballOnlyRoute({ element }) {
  const { sportFilter } = useConfig();
  if (sportFilter !== 'FOOTBALL') return <Navigate to="/teams" replace />;
  return element;
}

// Auth pages
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));

// Core pages
const Home = lazy(() => import('./pages/Home'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Profile = lazy(() => import('./pages/Profile'));

// Events
const Events = lazy(() => import('./pages/Events'));
const EventDetail = lazy(() => import('./pages/EventDetail'));
const CreateEvent = lazy(() => import('./pages/CreateEvent'));
const EditEvent = lazy(() => import('./pages/EditEvent'));

// Teams
const Teams = lazy(() => import('./pages/Teams'));
const TeamDetail = lazy(() => import('./pages/TeamDetail'));
const CreateTeam = lazy(() => import('./pages/CreateTeam'));

// Matches
const Matches = lazy(() => import('./pages/Matches'));
const LiveMatches = lazy(() => import('./pages/LiveMatches'));

// Profile sub-pages
const PastEvents = lazy(() => import('./pages/PastEvents'));
const KridaDiary = lazy(() => import('./pages/KridaDiary'));

// Social
const Feed = lazy(() => import('./pages/Feed'));
const PostDetail = lazy(() => import('./pages/PostDetail'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Bookmarks = lazy(() => import('./pages/Bookmarks'));
const Followers = lazy(() => import('./pages/Followers'));

// Coaches
const Coaches = lazy(() => import('./pages/Coaches'));
const CoachDetail = lazy(() => import('./pages/CoachDetail'));
const CoachDashboard = lazy(() => import('./pages/CoachDashboard'));

// Sports
const FormationCompare = lazy(() => import('./pages/FormationCompare'));
const Sports = lazy(() => import('./pages/Sports'));
const SportDetail = lazy(() => import('./pages/SportDetail'));
const About = lazy(() => import('./pages/About'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Blog = lazy(() => import('./pages/Blog'));
const Careers = lazy(() => import('./pages/Careers'));
const Help = lazy(() => import('./pages/Help'));
const Contact = lazy(() => import('./pages/Contact'));
const ApiDocs = lazy(() => import('./pages/ApiDocs'));
const Docs = lazy(() => import('./pages/Docs'));
const Status = lazy(() => import('./pages/Status'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Cookies = lazy(() => import('./pages/Cookies'));
const NotFound = lazy(() => import('./pages/NotFound'));

function App() {
  return (
    <Suspense fallback={<LoadingSpinner fullPage message="Loading..." />}>
      <Routes>
        <Route element={<Layout />}>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Events */}
          <Route path="/events" element={<Events />} />
          <Route path="/events/:eventId" element={<EventDetail />} />
          <Route
            path="/events/create"
            element={
              <ProtectedRoute requiredRole="ORGANIZER">
                <CreateEvent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/:eventId/edit"
            element={
              <ProtectedRoute requiredRole="ORGANIZER">
                <EditEvent />
              </ProtectedRoute>
            }
          />

          {/* Teams */}
          <Route path="/formation-compare" element={<FootballOnlyRoute element={<FormationCompare />} />} />
          <Route path="/teams" element={<Teams />} />
          <Route path="/teams/:teamId" element={<TeamDetail />} />
          <Route
            path="/teams/create"
            element={
              <ProtectedRoute>
                <CreateTeam />
              </ProtectedRoute>
            }
          />

          {/* Matches */}
          <Route path="/matches" element={<Matches />} />
          <Route path="/live" element={<LiveMatches />} />

          {/* Coaches */}
          <Route path="/coaches" element={<Coaches />} />
          <Route path="/coaches/:coachId" element={<CoachDetail />} />
          <Route
            path="/coach/dashboard"
            element={
              <ProtectedRoute>
                <CoachDashboard />
              </ProtectedRoute>
            }
          />

          {/* Social */}
          <Route path="/feed" element={<Feed />} />
          <Route path="/posts/:postId" element={<PostDetail />} />
          <Route path="/users/:userId" element={<UserProfile />} />
          <Route path="/users/:userId/followers" element={<Followers />} />
          <Route path="/users/:userId/following" element={<Followers />} />
          <Route
            path="/bookmarks"
            element={
              <ProtectedRoute>
                <Bookmarks />
              </ProtectedRoute>
            }
          />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/past-events/:type"
            element={
              <ProtectedRoute>
                <PastEvents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/diary"
            element={
              <ProtectedRoute>
                <KridaDiary />
              </ProtectedRoute>
            }
          />

          {/* Sports */}
          <Route path="/sports" element={<Sports />} />
          <Route path="/sports/:sportKey" element={<SportDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/help" element={<Help />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/api-docs" element={<ApiDocs />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="/status" element={<Status />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
